import { Result, Skeleton } from 'antd';
import { lazy, type ReactNode, Suspense, useEffect } from 'react';
import type { PublicSharePort } from '../../../quote-integration/publicShareRuntime';
import type { CaseShowcaseShareSnapshot } from '../mock/caseShowcaseShare';
import { useCpq, useCpqShellController } from '../state/CpqContext';
import { isCasePreviewEnabled, isCaseView } from '../state/caseFeature';
import type { AppView } from '../state/types';
import { CustomersPage } from './AdminPages';
import { CaseCustomerSharePage } from './CaseCustomerSharePage';
import { CaseLibraryPage } from './CaseLibraryPage';
import { CaseShareAdminPage } from './CaseShareAdminPage';
import { SharePage } from './SharePage';

const loadManagementPages = () =>
  import('./ManagementPages').then((module) => ({
    default: module.ManagementPages,
  }));
const loadQuoteWorkspace = () =>
  import('./QuoteWorkspace').then((module) => ({
    default: module.QuoteWorkspace,
  }));
const ManagementPages = lazy(loadManagementPages);
const QuoteWorkspace = lazy(loadQuoteWorkspace);

function ModuleFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{ padding: 24 }}
    >
      <Skeleton active title={{ width: 240 }} paragraph={{ rows: 6 }} />
    </div>
  );
}

function DeferredModule({ children }: { children: ReactNode }) {
  return <Suspense fallback={<ModuleFallback />}>{children}</Suspense>;
}

type RouteRenderer = (view: AppView, revision: number) => ReactNode;

const routeMap: Partial<Record<AppView, RouteRenderer>> = {
  cases: () => <CaseLibraryPage />,
  'case-admin': () => <CaseShareAdminPage />,
  workspace: (_, revision) => (
    <DeferredModule>
      <QuoteWorkspace key={`workspace-${revision}`} />
    </DeferredModule>
  ),
  share: (_, revision) => <SharePage key={`share-${revision}`} />,
  customers: () => <CustomersPage />,
};

const managementRoute: RouteRenderer = (view, revision) => (
  <DeferredModule>
    <ManagementPages key={`${view}-${revision}`} view={view} />
  </DeferredModule>
);

export function MainRouter({
  publicAccess,
  caseShowcaseShare,
}: {
  publicAccess?: { token: string; port: PublicSharePort };
  caseShowcaseShare?: CaseShowcaseShareSnapshot | null;
}) {
  if (caseShowcaseShare !== undefined && !isCasePreviewEnabled())
    return <Result status="404" title="链接不可用" />;
  return caseShowcaseShare !== undefined ? (
    <CaseCustomerSharePage snapshot={caseShowcaseShare} />
  ) : publicAccess ? (
    <SharePage publicAccess={publicAccess} />
  ) : (
    <InternalMainRouter />
  );
}

function InternalMainRouter() {
  const controller = useCpqShellController();
  const { resetEpoch } = useCpq();
  useEffect(() => {
    const preload = () => {
      void loadManagementPages();
      void loadQuoteWorkspace();
    };
    const idleWindow = window as unknown as {
      requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions,
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (idleWindow.requestIdleCallback) {
      const idleId = idleWindow.requestIdleCallback(preload, {
        timeout: 2_000,
      });
      return () => idleWindow.cancelIdleCallback?.(idleId);
    }
    const timeoutId = window.setTimeout(preload, 1_000);
    return () => window.clearTimeout(timeoutId);
  }, []);
  if (isCaseView(controller.activeView) && !isCasePreviewEnabled())
    return <Result status="404" title="页面不可用" />;
  return (routeMap[controller.activeView] ?? managementRoute)(
    controller.activeView,
    resetEpoch,
  );
}
